import { memo, type ReactNode } from "react";
import { GFM, parser as baseParser } from "@lezer/markdown";
import type { SyntaxNode } from "@lezer/common";
import { justOneSyntax } from "@justone/editor/syntax";
import { MentionChip, WikiLinkChip } from "./Previews";
import { TaskRefChip } from "./TaskBits";

const parser = baseParser.configure([GFM, justOneSyntax]);

const SKIP = new Set(["EmphasisMark", "StrikethroughMark", "CodeMark", "LinkMark", "WikiLinkMark", "MentionMark", "QuoteMark", "ListMark", "HeaderMark", "CodeInfo", "TaskMarker", "TableDelimiter"]);

function safeHref(raw: string): string | null {
  const href = raw.trim().replace(/^<|>$/g, "");
  return /^(https?:|mailto:)/i.test(href) ? href : null;
}

interface Ctx {
  text: string;
  /** 내 아이디·이름: 나를 멘션하면 강조 */
  me: Set<string>;
  /** [[위키 링크]]의 "같은 공간" = 이 채널의 위키 (DM이면 공통 위키) */
  channelId: number | null;
}

/**
 * 채팅 메시지용 마크다운 렌더러. 위키 편집기와 같은 lezer 문법(위키 링크·멘션·할일 참조)을 쓴다.
 * React 엘리먼트로 직접 만들므로 HTML 주입이 불가능하다.
 */
export const MessageBody = memo(function MessageBody({ body, me, channelId = null }: { body: string; me: Set<string>; channelId?: number | null }) {
  const tree = parser.parse(body);
  const ctx: Ctx = { text: body, me, channelId };
  return <div className="md">{blocks(ctx, tree.topNode)}</div>;
});

const slice = (ctx: Ctx, from: number, to: number) => ctx.text.slice(from, to);

function children(node: SyntaxNode): SyntaxNode[] {
  const out: SyntaxNode[] = [];
  for (let c = node.firstChild; c; c = c.nextSibling) out.push(c);
  return out;
}

function blocks(ctx: Ctx, parent: SyntaxNode): ReactNode[] {
  return children(parent).map((n, i) => block(ctx, n, i));
}

function block(ctx: Ctx, n: SyntaxNode, key: number): ReactNode {
  switch (n.name) {
    case "Paragraph":
      return <p key={key}>{inline(ctx, n)}</p>;
    case "ATXHeading1":
    case "ATXHeading2":
    case "ATXHeading3":
    case "ATXHeading4":
    case "ATXHeading5":
    case "ATXHeading6":
    case "SetextHeading1":
    case "SetextHeading2":
      return (
        <p key={key} className="heading">
          {inline(ctx, n).map((x, i) => (typeof x === "string" && i === 0 ? x.trimStart() : x))}
        </p>
      );
    case "BulletList":
      return <ul key={key}>{blocks(ctx, n)}</ul>;
    case "OrderedList":
      return <ol key={key}>{blocks(ctx, n)}</ol>;
    case "ListItem": {
      const task = n.getChild("Task");
      if (task) {
        const marker = task.getChild("TaskMarker");
        const checked = marker ? /x/i.test(slice(ctx, marker.from, marker.to)) : false;
        return (
          <li key={key} className="task">
            <input type="checkbox" checked={checked} readOnly disabled aria-label={checked ? "완료" : "미완료"} />
            {inline(ctx, task, marker ? marker.to : task.from)}
          </li>
        );
      }
      return <li key={key}>{blocks(ctx, n)}</li>;
    }
    case "Blockquote":
      return <blockquote key={key}>{blocks(ctx, n)}</blockquote>;
    case "FencedCode":
    case "CodeBlock": {
      const code = n.getChild("CodeText");
      return (
        <pre key={key}>
          <code>{code ? slice(ctx, code.from, code.to) : ""}</code>
        </pre>
      );
    }
    case "HorizontalRule":
      return <hr key={key} />;
    case "Table":
      return table(ctx, n, key);
    case "ListMark":
    case "QuoteMark":
      return null;
    default:
      // HTML 블록 등은 글자 그대로 보여준다.
      return <p key={key}>{slice(ctx, n.from, n.to)}</p>;
  }
}

function table(ctx: Ctx, n: SyntaxNode, key: number): ReactNode {
  const row = (r: SyntaxNode, Cell: "th" | "td", i: number) => (
    <tr key={i}>
      {r.getChildren("TableCell").map((c, j) => (
        <Cell key={j}>{inline(ctx, c)}</Cell>
      ))}
    </tr>
  );
  const header = n.getChild("TableHeader");
  return (
    <table key={key}>
      {header && <thead>{row(header, "th", 0)}</thead>}
      <tbody>{n.getChildren("TableRow").map((r, i) => row(r, "td", i))}</tbody>
    </table>
  );
}

/** 노드 안의 인라인 내용. 자식 노드 사이의 빈틈은 일반 텍스트 (줄바꿈은 CSS pre-wrap으로 유지). */
function inline(ctx: Ctx, parent: SyntaxNode, from = parent.from, to = parent.to): ReactNode[] {
  const out: ReactNode[] = [];
  let pos = from;
  let i = 0;
  for (let c = parent.firstChild; c; c = c.nextSibling) {
    if (c.to <= from || c.from >= to) continue;
    if (c.from > pos) out.push(slice(ctx, pos, c.from));
    const rendered = inlineNode(ctx, c, i++);
    if (rendered !== null) out.push(rendered);
    pos = c.to;
  }
  if (pos < to) out.push(slice(ctx, pos, to));
  return out;
}

function inlineNode(ctx: Ctx, n: SyntaxNode, key: number): ReactNode {
  if (SKIP.has(n.name)) return null;
  switch (n.name) {
    case "Emphasis":
      return <em key={key}>{inline(ctx, n)}</em>;
    case "StrongEmphasis":
      return <strong key={key}>{inline(ctx, n)}</strong>;
    case "Strikethrough":
      return <s key={key}>{inline(ctx, n)}</s>;
    case "InlineCode":
      return <code key={key}>{inline(ctx, n)}</code>;
    case "Link": {
      const marks = n.getChildren("LinkMark");
      const url = n.getChild("URL");
      const href = url ? safeHref(slice(ctx, url.from, url.to)) : null;
      const label = inline(ctx, n, marks[0]?.to ?? n.from, marks[1]?.from ?? n.to);
      return href ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      ) : (
        <span key={key}>{label}</span>
      );
    }
    case "URL":
    case "Autolink": {
      const url = n.name === "URL" ? n : n.getChild("URL");
      const raw = url ? slice(ctx, url.from, url.to) : slice(ctx, n.from, n.to);
      const href = safeHref(raw);
      return href ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {raw}
        </a>
      ) : (
        raw
      );
    }
    case "Image": {
      // M1: 채팅에서는 이미지를 불러오지 않고 링크로 보여준다 (첨부는 M2).
      const marks = n.getChildren("LinkMark");
      const url = n.getChild("URL");
      const alt = slice(ctx, marks[0]?.to ?? n.from, marks[1]?.from ?? n.to) || "이미지";
      const href = url ? safeHref(slice(ctx, url.from, url.to)) : null;
      return href ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {alt}
        </a>
      ) : (
        alt
      );
    }
    case "WikiLink":
      return <WikiLinkChip key={key} inner={slice(ctx, n.from + 2, n.to - 2)} channelId={ctx.channelId} />;
    case "Mention": {
      const name = slice(ctx, n.from + 1, n.to);
      return <MentionChip key={key} name={name} isMe={ctx.me.has(name.toLowerCase())} />;
    }
    case "TaskRef":
      return <TaskRefChip key={key} id={Number(slice(ctx, n.from + 3, n.to))} />;
    case "Escape":
      return slice(ctx, n.from + 1, n.to);
    case "HardBreak":
      return <br key={key} />;
    default:
      return n.firstChild ? <span key={key}>{inline(ctx, n)}</span> : slice(ctx, n.from, n.to);
  }
}
